import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, ChevronDown } from 'lucide-react'

// Supported languages - must match i18n config in consuming apps
const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
] as const

type LanguageCode = (typeof supportedLanguages)[number]['code']

/**
 * LanguageSwitcher Component
 *
 * A dropdown component for switching between supported languages.
 * Placed in the header, top-right area near the dark mode toggle.
 *
 * Features:
 * - Displays current language with globe icon
 * - Dropdown with all supported languages showing native names
 * - Persists selection to localStorage via i18next
 * - Closes on click outside or escape key
 */
const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const currentLanguage = supportedLanguages.find(
    (lang) => lang.code === i18n.language
  ) || supportedLanguages[0]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleLanguageChange = (langCode: LanguageCode) => {
    i18n.changeLanguage(langCode)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 bg-primary p-1.5 text-secondary rounded-full hover:text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors"
        aria-label="Select language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="h-5 w-5" aria-hidden="true" />
        <span className="text-xs font-medium uppercase hidden sm:inline">
          {currentLanguage.code}
        </span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-primary ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
          role="listbox"
          aria-label="Select language"
        >
          <div className="py-1">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-secondary transition-colors flex items-center justify-between ${
                  lang.code === currentLanguage.code
                    ? 'text-accent font-medium bg-secondary/50'
                    : 'text-primary'
                }`}
                role="option"
                aria-selected={lang.code === currentLanguage.code}
              >
                <span>{lang.nativeName}</span>
                {lang.code === currentLanguage.code && (
                  <span className="text-accent">✓</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
